import { ReactNode, Suspense, useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useEntitlement } from "../../contexts/EntitlementContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { authFetch, clearPersistedApiCache } from "../../utils/authFetch";
import { useRole } from "../../contexts/RoleContext";
import { identityEmail } from "../../utils/memberSession";
import AppTutorial from "../../components/AppTutorial";
import { MANAGER_TOUR_STEPS } from "../../components/managerTourSteps";

function managerTutorialSeenKey(tenantId: string, email: string): string {
  return `sherloxaware_manager_tutorial_seen_${tenantId}_${email}`;
}

interface ManagerLayoutProps {
  children: ReactNode;
}

const overviewIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z" />
  </svg>
);

const reportsIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625zM7.5 15a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 017.5 15zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H8.25z" clipRule="evenodd" />
    <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
  </svg>
);

export default function ManagerLayout({ children }: ManagerLayoutProps) {
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialSidebar, setTutorialSidebar] = useState(false);
  const sidebarOpen = sidebarOpenMobile || sidebarHovered || tutorialSidebar;
  const setSidebarOpen = setSidebarOpenMobile;
  const [isMobile, setIsMobile] = useState(false);
  const { instance, accounts } = useMsal();
  const { isDark, toggleTheme } = useTheme();
  const { entitlement } = useEntitlement();
  const { t, language } = useLanguage();
  const account = instance.getActiveAccount() ?? accounts[0];
  const { tenantId } = useRole();
  const userEmail = identityEmail(account?.username).toLowerCase().trim();

  const navItems = [
    { path: "/manager", label: t.managerTeam.title, icon: overviewIcon, end: true },
    { path: "/manager/berichte", label: t.managerReports.title, icon: reportsIcon, end: false },
  ];

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpenMobile(false);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const openTutorial = () => setTutorialOpen(true);

  // Einführungs-Tour beim ersten Öffnen des Manager-Bereichs.
  useEffect(() => {
    if (!tenantId || !userEmail || userEmail === "unknown") return;
    const seenKey = managerTutorialSeenKey(tenantId, userEmail);
    try { if (localStorage.getItem(seenKey)) return; } catch {}
    let cancelled = false;
    authFetch(`/api/user-profiles?tenantId=${encodeURIComponent(tenantId)}&email=${encodeURIComponent(userEmail)}`)
      .then(r => r.ok ? r.json() : null)
      .then((profile: { managerTutorialSeenAt?: string } | null) => {
        if (cancelled || !profile) return;
        if (profile.managerTutorialSeenAt) {
          try { localStorage.setItem(seenKey, "1"); } catch {}
          return;
        }
        openTutorial();
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tenantId, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeTutorial = () => {
    setTutorialOpen(false);
    if (!tenantId || !userEmail) return;
    try { localStorage.setItem(managerTutorialSeenKey(tenantId, userEmail), "1"); } catch {}
    authFetch("/api/user-profiles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, email: userEmail, managerTutorialSeen: true }),
    }).catch(() => {});
  };

  const forceTutorialSidebar = useCallback((open: boolean) => setTutorialSidebar(open), []);

  const handleLogout = () => {
    clearPersistedApiCache();
    instance.logoutRedirect();
  };

  const cobrandLogo = isDark
    ? (entitlement?.features.cobrand_logo_dark ?? entitlement?.features.cobrand_logo)
    : (entitlement?.features.cobrand_logo ?? entitlement?.features.cobrand_logo_dark);
  const cobrandOnDark = isDark && !!entitlement?.features.cobrand_logo_dark;

  return (
    <div id="manager-shell" style={{ display: "flex", height: "100vh", background: "var(--bg-page)", overflow: "hidden", position: "relative" }}>
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150 }} />
      )}
      <aside
        id="manager-sidebar"
        onMouseEnter={() => { if (!isMobile) setSidebarHovered(true); }}
        onMouseLeave={() => setSidebarHovered(false)}
        style={{
          width: isMobile ? "240px" : (sidebarOpen ? "240px" : "64px"),
          background: isDark ? "#161B22" : "#1B6AB1",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflow: "hidden",
          willChange: "width",
          transition: "width 160ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 160ms ease",
          boxShadow: !isMobile && sidebarHovered ? "4px 0 16px rgba(0,0,0,0.18)" : "none",
          ...(isMobile
            ? { position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 200, transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)" }
            : { position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 100 }),
        }}
      >
        <div style={{ width: isMobile ? "100%" : (sidebarOpen ? "240px" : "64px"), display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
          <div style={{ boxSizing: "border-box", padding: "0 0.875rem", borderBottom: "1px solid rgba(255,255,255,0.12)", flexShrink: 0 }}>
            <div style={{ height: "56px", display: "flex", alignItems: "center", gap: "0.75rem", justifyContent: "flex-start" }}>
              <div style={{ width: "36px", height: "36px", background: "#fff", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
                <img src="/brand/aware-icon.png" alt="SHERLOX AWARE" width="30" height="30" style={{ display: "block", objectFit: "contain" }} />
              </div>
              {sidebarOpen && (
                <div style={{ minWidth: 0 }}>
                  <div style={{ lineHeight: 1.1, whiteSpace: "nowrap" }}>
                    <span style={{ fontWeight: "700", fontSize: "1rem", color: "white" }}>SHERLOX</span>
                    <span style={{ fontWeight: "400", fontSize: "1rem", color: "white" }}> AWARE</span>
                  </div>
                  <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.65)", marginTop: "2px", letterSpacing: "0.03em", whiteSpace: "nowrap" }}>
                    {t.managerTeam.shellSubtitle}
                  </div>
                </div>
              )}
            </div>
          </div>

          <nav data-tour="nav" style={{ padding: "0.75rem 0.5rem", flex: 1, overflowY: "auto", minHeight: 0 }}>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                data-tour={`nav-${item.path.split("/").pop()}`}
                onClick={() => { if (isMobile) setSidebarOpen(false); }}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: sidebarOpen ? "0.625rem" : "0",
                  padding: "0.6rem 0.75rem",
                  borderRadius: "6px",
                  marginBottom: "2px",
                  color: isDark ? (isActive ? "#F97316" : "rgba(240,246,252,0.75)") : "white",
                  background: isActive ? (isDark ? "rgba(249,115,22,0.15)" : "#1559A0") : "transparent",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  fontWeight: isActive ? "600" : "400",
                  transition: "background 0.15s ease",
                  justifyContent: "flex-start",
                })}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  if (el.getAttribute("aria-current") !== "page") el.style.background = "rgba(255,255,255,0.12)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  if (el.getAttribute("aria-current") !== "page") el.style.background = "transparent";
                }}
              >
                {item.icon}
                {sidebarOpen && item.label}
              </NavLink>
            ))}

            <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid rgba(255,255,255,0.12)" }}>
              <NavLink
                to="/dashboard"
                data-tour="nav-employee"
                onClick={() => { if (isMobile) setSidebarOpen(false); }}
                style={() => ({
                  display: "flex",
                  alignItems: "center",
                  gap: sidebarOpen ? "0.625rem" : "0",
                  padding: "0.6rem 0.75rem",
                  borderRadius: "6px",
                  color: "rgba(255,255,255,0.75)",
                  background: "transparent",
                  textDecoration: "none",
                  fontSize: "0.8rem",
                  fontWeight: "400",
                  justifyContent: "flex-start",
                })}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                </svg>
                {sidebarOpen && t.managerTeam.backToDashboard}
              </NavLink>
            </div>
          </nav>

          {sidebarOpen && cobrandLogo && (
            <div style={{ flexShrink: 0, padding: "0.625rem 0.875rem", borderTop: "1px solid rgba(255,255,255,0.12)" }}>
              <div style={{ background: cobrandOnDark ? "rgba(255,255,255,0.08)" : "#fff", borderRadius: "6px", padding: "5px 8px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
                <img src={cobrandLogo} alt={entitlement?.features.cobrand_name ?? "Logo"} style={{ maxHeight: "30px", maxWidth: "100%", objectFit: "contain", display: "block" }} />
              </div>
            </div>
          )}

          <div style={{ padding: "0.875rem 0.5rem", borderTop: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.15)", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", justifyContent: "flex-start" }}>
              <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.875rem", fontWeight: "700", color: "white", flexShrink: 0 }}>
                {account?.name?.charAt(0)?.toUpperCase() ?? "M"}
              </div>
              {sidebarOpen && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.8rem", color: "white", fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {account?.name ?? "Manager"}
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {account?.username ?? ""}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      <div id="manager-content" style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, height: "100vh", overflow: "hidden", marginLeft: isMobile ? 0 : "64px" }}>
        <header id="manager-topbar" style={{ height: "56px", background: "var(--bg-card)", borderBottom: "1px solid var(--border)", boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1rem", gap: "0.75rem", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", background: "transparent", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--text-secondary)", cursor: "pointer", flexShrink: 0, marginRight: "0.25rem" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path fillRule="evenodd" d="M3 6.75A.75.75 0 013.75 6h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 6.75zM3 12a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 12zm0 5.25a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75a.75.75 0 01-.75-.75z" clipRule="evenodd" /></svg>
              </button>
            )}
            {!isMobile && (
              <>
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#1B6AB1" }} />
                <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", fontWeight: "500" }}>{t.managerTeam.modeActive}</span>
              </>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button
              data-tour="help"
              onClick={openTutorial}
              title={language === "en" ? "Tour & help" : "Einführung & Hilfe"}
              aria-label={language === "en" ? "Open tour" : "Einführung öffnen"}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", borderRadius: "8px", background: "transparent", border: "1px solid var(--border)", color: "var(--text-secondary)", cursor: "pointer", transition: "all 0.15s ease", flexShrink: 0, fontSize: "0.95rem", fontWeight: 700, fontFamily: "inherit" }}
              onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-card-alt)"; }}
              onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              ?
            </button>
            <button
              data-tour="theme"
              onClick={toggleTheme}
              title={isDark ? "Hellmodus" : "Dunkelmodus"}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", borderRadius: "8px", background: "transparent", border: "1px solid var(--border)", color: "var(--text-secondary)", cursor: "pointer", transition: "all 0.15s ease", flexShrink: 0 }}
              onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-card-alt)"; }}
              onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              {isDark ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.166 17.834a.75.75 0 00-1.06 1.06l1.59 1.591a.75.75 0 001.061-1.06l-1.59-1.591zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.166 6.166a.75.75 0 001.06 1.06l1.591-1.59a.75.75 0 10-1.06-1.061l-1.591 1.59z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            <button
              onClick={handleLogout}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.375rem 0.875rem", background: "transparent", border: "1px solid #1B6AB1", borderRadius: "6px", color: "#1B6AB1", fontSize: "0.8rem", cursor: "pointer", fontWeight: "500", transition: "all 0.15s ease" }}
              onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#1B6AB1"; (e.currentTarget as HTMLButtonElement).style.color = "#ffffff"; }}
              onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.color = "#1B6AB1"; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9A.75.75 0 0115 9V5.25a1.5 1.5 0 00-1.5-1.5h-6zm10.72 4.72a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H9a.75.75 0 010-1.5h10.94l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
              {language === "en" ? "Log out" : "Abmelden"}
            </button>
          </div>
        </header>

        <main id="manager-main" style={{ flex: 1, padding: isMobile ? "1rem" : "1.5rem", overflow: "auto", background: "var(--bg-page)" }}>
          <Suspense fallback={
            <div style={{ minHeight: "40vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
                <div style={{ width: "32px", height: "32px", border: "3px solid var(--border)", borderTop: "3px solid #1B6AB1", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <span>{t.common.loading}</span>
              </div>
            </div>
          }>
            {children}
          </Suspense>
        </main>
      </div>

      <AppTutorial
        open={tutorialOpen}
        language={language}
        steps={MANAGER_TOUR_STEPS}
        finishLabel={{ de: "Los geht's", en: "Let's go" }}
        onClose={closeTutorial}
        onFinish={closeTutorial}
        onSidebarForce={forceTutorialSidebar}
      />
    </div>
  );
}
